import { RootState } from '@AppState/redux-store'
import { JsonView } from '@Components/common/json-view'
import { SideDrawer } from '@Components/common/side-drawer'
import { readErrorMessage } from '@Components/util/utils'
import { ConfigChangeConfigResponse, ConfigChangeResponse } from '@Generated/back-office-client/src'
import { EyeClosedIcon, EyeOpenIcon } from '@radix-ui/react-icons'
import {
  AlertDialog,
  Badge,
  Button,
  Flex,
  Heading,
  IconButton,
  Table,
  Text,
  Tooltip,
} from '@radix-ui/themes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatInTimeZone } from 'date-fns-tz'
import { useState } from 'react'
import { useSelector } from 'react-redux'

type ConfigHistoryDrawerProps<T extends ConfigChangeResponse> = {
  title: string
  /** Cache key for the history list; refetched after a restore */
  queryKey: readonly unknown[]
  listHistory: () => Promise<T[]>
  getVersion: (changeId: string) => Promise<ConfigChangeConfigResponse>
  restoreVersion: (changeId: string) => Promise<unknown>
  /** Hide config values until revealed, for configs that hold credentials */
  maskConfig?: boolean
  /** Extra detail shown for each version, such as the plugin it used */
  describeVersion?: (change: T) => string
  onRestored: () => void
  onClose: () => void
}

/** Lists the saved versions of a config, newest first, and restores an earlier one. */
export const ConfigHistoryDrawer = <T extends ConfigChangeResponse>({
  title,
  queryKey,
  listHistory,
  getVersion,
  restoreVersion,
  maskConfig = false,
  describeVersion,
  onRestored,
  onClose,
}: ConfigHistoryDrawerProps<T>) => {
  const queryClient = useQueryClient()
  const { activeCustomerMetadata } = useSelector((state: RootState) => state.users)
  const timezone = activeCustomerMetadata?.timezone ?? 'UTC'

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [revealed, setRevealed] = useState(false)
  const [restoreTarget, setRestoreTarget] = useState<T | null>(null)
  const [restoreError, setRestoreError] = useState<string | null>(null)

  const { data: history, isLoading } = useQuery({ queryKey, queryFn: listHistory })

  const { data: selectedVersion, isLoading: versionLoading } = useQuery({
    queryKey: [...queryKey, selectedId],
    queryFn: () => getVersion(selectedId!),
    enabled: !!selectedId,
  })

  const restore = useMutation({
    mutationFn: (changeId: string) => restoreVersion(changeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      setRestoreTarget(null)
      setSelectedId(null)
      onRestored()
    },
    onError: async (err: unknown) => {
      setRestoreError(await readErrorMessage(err, 'This version could not be restored.'))
    },
  })

  const formatSavedAt = (createdAt: Date) => formatInTimeZone(createdAt, timezone, 'PP p')

  const selectVersion = (changeId: string) => {
    setSelectedId(changeId)
    setRevealed(false)
  }

  const openRestore = (change: T) => {
    setRestoreError(null)
    setRestoreTarget(change)
  }

  const renderSelectedConfig = () => {
    if (!selectedId) {
      return null
    }
    if (versionLoading || !selectedVersion) {
      return <Text>Loading...</Text>
    }
    if (!selectedVersion.config) {
      return <Text color="gray">This version has no config.</Text>
    }
    return (
      <Flex direction="column" gap="2">
        <Flex align="center" justify="between">
          <Heading size="3">Saved {formatSavedAt(selectedVersion.createdAt)}</Heading>
          {maskConfig && (
            <Tooltip content={revealed ? 'Hide values' : 'Reveal values'}>
              <IconButton
                variant="soft"
                aria-label={revealed ? 'Hide values' : 'Reveal values'}
                onClick={() => setRevealed(!revealed)}>
                {revealed ? <EyeClosedIcon /> : <EyeOpenIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Flex>
        {maskConfig && !revealed ? (
          <Text color="gray">••••••••</Text>
        ) : (
          <JsonView data={selectedVersion.config} />
        )}
      </Flex>
    )
  }

  const renderRows = () => {
    return history?.map((change, index) => (
      <Table.Row key={change.id}>
        <Table.Cell>
          <Flex direction="column" gap="1">
            <Text>{formatSavedAt(change.createdAt)}</Text>
            <Flex gap="1">
              {index === 0 && <Badge color="green">Current</Badge>}
              {change.restoredFromId && <Badge color="blue">Restored</Badge>}
            </Flex>
          </Flex>
        </Table.Cell>
        <Table.Cell style={{ wordBreak: 'break-all' }}>
          <Text>{change.changedBy ?? 'Unknown'}</Text>
          {describeVersion && (
            <Text as="p" size="1" color="gray">
              {describeVersion(change)}
            </Text>
          )}
        </Table.Cell>
        <Table.Cell>
          <Flex gap="2">
            <Button size="1" variant="soft" onClick={() => selectVersion(change.id)}>
              View
            </Button>
            {index > 0 && (
              <Button size="1" variant="soft" color="orange" onClick={() => openRestore(change)}>
                Restore
              </Button>
            )}
          </Flex>
        </Table.Cell>
      </Table.Row>
    ))
  }

  const renderHistory = () => {
    if (isLoading) {
      return <Text>Loading...</Text>
    }
    if (!history?.length) {
      return <Text color="gray">No saved versions yet.</Text>
    }
    return (
      <Table.Root size="1">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Saved</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>By</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell />
          </Table.Row>
        </Table.Header>
        <Table.Body>{renderRows()}</Table.Body>
      </Table.Root>
    )
  }

  return (
    <SideDrawer onClose={onClose}>
      <Flex direction="column" gap="3">
        <Heading size="4">{title}</Heading>
        {renderHistory()}
        {renderSelectedConfig()}
      </Flex>

      <AlertDialog.Root
        open={!!restoreTarget}
        onOpenChange={(open) => (!open ? setRestoreTarget(null) : null)}>
        <AlertDialog.Content maxWidth="450px">
          <AlertDialog.Title>Restore this version?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            The version saved {restoreTarget ? formatSavedAt(restoreTarget.createdAt) : ''} will
            replace the current config. The restore is saved as a new version, so it can be undone
            the same way.
          </AlertDialog.Description>

          {restoreError && (
            <Text as="p" size="2" mt="2" color="red">
              {restoreError}
            </Text>
          )}

          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <Button
              color="orange"
              loading={restore.isPending}
              onClick={() => restoreTarget && restore.mutate(restoreTarget.id)}>
              Restore
            </Button>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </SideDrawer>
  )
}
